import { WhichWeightAPI } from 'WhichWeightAPI'
import { useState, useEffect, useContext } from 'react'
import { ModalKey, ModalContext } from 'components/Modals/ModalContext'
import { useSelector, useDispatch } from 'react-redux'
import { resetTrainingChartState, initializeTrainingChartData } from 'redux/slices/trainingChart'
import { BulkEditViewContext } from 'views/Athlete/BulkEditView'
import { useGetExercises } from 'redux/customHooks/useGetExercises'
import { IsExerciseSetUnitSeconds } from 'functions/exerciseUtils'

import ScreenFrame from 'components/Frame/ScreenFrame'
import FitnessProfileInputContainer from 'components/ProgramGenerator/InputContainers/FitnessProfileInputContainer'
import EventsInputContainer from 'components/ProgramGenerator/InputContainers/EventsInputContainer'
import AssessmentInputContainer from 'components/ProgramGenerator/InputContainers/AssessentInputContainer'
import ProgressionModifiers from 'components/ProgramGenerator/InputContainers/ProgressionModifiers'
import OverridesInputContainer from 'components/ProgramGenerator/InputContainers/OverridesInputContainer'
import InjuriesInputContainerGenerator from 'components/ProgramGenerator/InputContainers/InjuriesInputContainerGenerator'
import LoadingButton from 'components/LoadingButton'
import ExerciseProgressionConfig from 'components/TrainingChart/ExerciseProgressionConfig'
import TrainingChart from 'components/TrainingChart/TrainingChart'

export default function ProgramSnapshotGenerator() {

    const dispatch = useDispatch()

    const { exercises } = useGetExercises()

    const { ShowModal } = useContext(ModalContext)

    const userId = useSelector((state) => state.user.id)
    const sport = useSelector((state) => state.generator.inputs.sport)
    const assessment = useSelector((state) => state.generator.inputs.assessment)
    const progressionLevel = useSelector((state) => state.generator.inputs.progressionLevel)
    const progressionLevelScore = useSelector((state) => state.generator.inputs.progressionLevelScore)
    const aRace = useSelector((state) => state.generator.inputs.aRace)
    const bAndCRaces = useSelector((state) => state.generator.inputs.bAndCRaces)
    const equipmentProfile = useSelector((state) => state.generator.inputs.equipmentProfile)
    const volumeProgressionModifier = useSelector((state) => state.generator.inputs.volumeProgressionModifier)
    const workoutTemplateExerciseProgressionModifiers = useSelector((state) => state.generator.inputs.workoutTemplateExerciseProgressionModifiers)
    const overrides = useSelector((state) => state.generator.inputs.overrides)
    const injuries = useSelector((state) => state.generator.inputs.injuries)
    const latestProgramSnapshot = useSelector((state) => state.trainingChart.latestProgramSnapshot)

    const [programSnapshotLoading, setProgramSnapshotLoading] = useState(false)
    
    const GenerateProgramSnapshot = async () => {

        setProgramSnapshotLoading(true)

        const jsonBody = {
            userId: userId,
            assessment: JSON.stringify(assessment),
            sport: sport,
            progressionLevel: progressionLevel,
            progressionLevelScore: progressionLevelScore,
            aRace: JSON.stringify(aRace),
            bAndCRaces: JSON.stringify(bAndCRaces),
            equipmentProfile: JSON.stringify(equipmentProfile),
            volumeProgressionModifier: volumeProgressionModifier,
            workoutTemplateExerciseProgressionModifiers: JSON.stringify(workoutTemplateExerciseProgressionModifiers),
            overrides: JSON.stringify(overrides),
            injuries: injuries,
        }

        // console.log(jsonBody);

        const result = await WhichWeightAPI('PROGRAM', 'POST', '/coach_dashboard_generate_program_snapshot', jsonBody)

        // console.log(result.data.data);

        if (result.data.status === 'failure') {
            console.error(result)
            dispatch(resetTrainingChartState())
            setProgramSnapshotLoading(false)
            return
        }

        const payload = {
            athleteId: 0, // athlete id not needed for program snapshot generator
            trainingChartUILocation: 'snapshot-generator',
            ...result.data.data,
        }

        dispatch(initializeTrainingChartData(payload))

        setProgramSnapshotLoading(false)

    }

    const OnStartOverrideDrag = (event, dragType, weekOfTheYearKey, workoutTemplateKey, phaseIndex, volumeData, slotIndex, exercise) => { }

    const OnOverrideDragEntersCell = (event, dragType, weekOfTheYearKey, workoutTemplateKey, phaseIndex, volumeData, slotIndex, exercise) => { }

    const OnEndOverrideDrag = () => { };

    const GetPhaseCellStyle = (weekOfTheYearKey, workoutTemplateKey, phaseIndex) => {
        var style = 'w-full flex items-center p-2 border-b border-gray-400'
        style += ' bg-gray-100'
        return style
    }

    const GetSlotCellStyle = (weekOfTheYearKey, workoutTemplateKey, phaseIndex, slotIndex) => {
        var style = 'h-24 p-2 flex flex-col overflow-y-scroll relative'
        style += ' [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]'

        var currentExericse = latestProgramSnapshot.programTable.find(week => week.weekOfTheYearKey === weekOfTheYearKey).exerciseData[workoutTemplateKey][phaseIndex].slots[slotIndex]

        var progressionLevel = parseInt(currentExericse.progressionLevel)

        // Highlight missing exercise cells red

        if (currentExericse.id === -1) {
            style += ' bg-red-400'
            return style
        }

        if (progressionLevel === 0) style += ' bg-orange-100'
        else if (progressionLevel === 1) style += ' bg-orange-200'
        else if (progressionLevel === 2) style += ' bg-orange-300'
        else if (progressionLevel === 3) style += ' bg-orange-400'
        else if (progressionLevel === 4) style += ' bg-orange-500'

        return style
    }

    const GetProgramTableDisplayData = (weekOfTheYearKey, workoutTemplateKey, overrideKey, phaseIndex, slotIndex = 0) => {

        const programTableColumn = latestProgramSnapshot.programTable.find(week => week.weekOfTheYearKey === weekOfTheYearKey)

        switch (overrideKey) {

            case 'one-off-sets-override':
                return {
                    overrideApplied: false,
                    value: programTableColumn.volumeData.setsAndRepsData[workoutTemplateKey][phaseIndex].sets
                }

            case 'one-off-exercise-swap':

                const originalExerciseId = programTableColumn.exerciseData[workoutTemplateKey][phaseIndex].slots[slotIndex].id

                const missingExerciseObject = {
                    id: -1,
                    name: 'MISSING EXERCISE',
                    lateral: 'bilateral',
                    progressionLevel: 0,
                }

                const databaseExercise = exercises.find(e => e.id === originalExerciseId) || JSON.parse(JSON.stringify(missingExerciseObject))

                return {
                    overrideApplied: false,
                    value: databaseExercise
                }

            case 'one-off-reps-override':

                const slotConfig = latestProgramSnapshot.slotExerciseProgressionConfig[workoutTemplateKey].phases[phaseIndex].slots[slotIndex]
                const phaseVolumeData = programTableColumn.volumeData.setsAndRepsData[workoutTemplateKey][phaseIndex]
                const originalExercise = programTableColumn.exerciseData[workoutTemplateKey][phaseIndex].slots[slotIndex]

                // Get exercise lateral. Default to bilateral if not found.

                var originalExerciseLateral = originalExercise.lateral

                if (
                    originalExerciseLateral !== 'unilateral' &&
                    originalExerciseLateral !== 'bilateral'
                ) {
                    var tempDatabaseExercise = exercises.find(e => e.id === originalExercise.id) || null
                    originalExerciseLateral = tempDatabaseExercise.lateral
                }

                if (
                    originalExerciseLateral !== 'unilateral' &&
                    originalExerciseLateral !== 'bilateral'
                ) {
                    console.error('Could not find exercise lateral for exercise:', originalExercise.id)
                    originalExerciseLateral = 'bilateral'
                }

                // TODO: Change this
                // Hard coding seconds exercises since getting database exercise for every cell is inefficent
                // We should be caching setUnit

                if (IsExerciseSetUnitSeconds(originalExercise.id) === true) {
                    
                    if (
                        originalExercise.id === 386 // Front Side Flutter
                        || originalExercise.id === 387 // Back Side Flutter
                    ) {
                        return {
                            overrideApplied: false,
                            value: 30
                        }
                    }

                    return {
                        overrideApplied: false,
                        value: 10
                    }
                }

                // TODO: Change this
                // Displaying plyometric volume override in program table
                // We should really be caching category. This method works for now but is inefficent and could miss cells (exercise override for example)

                if (slotConfig?.progressionFilters?.category && slotConfig.progressionFilters.category.includes('plyometric')) {
                    
                    const originalDatabaseExercise = exercises.find(e => e.id === originalExercise.id) || null

                    // console.log(originalDatabaseExercise)

                    if (
                        originalDatabaseExercise.category === 'plyometric-bilateral'
                        || originalDatabaseExercise.category === 'plyometric-unilateral'
                    ) {

                        const plyometricOverrideData = programTableColumn.volumeData.plyometricSetsAndRepsOverrideData

                        // console.log(plyometricOverrideData)

                        return {
                            overrideApplied: false,
                            value: plyometricOverrideData[originalExerciseLateral]
                        }
                    }

                }

                // Slot specific reps data

                if (originalExerciseLateral === 'unilateral') {
                    return {
                        overrideApplied: false,
                        value: phaseVolumeData.reps[slotIndex].unilateral
                    }
                }
        
                if (originalExerciseLateral === 'bilateral') {
                    return {
                        overrideApplied: false,
                        value: phaseVolumeData.reps[slotIndex].bilateral
                    }
                }
        
                return 0

            case 'one-off-weight-recommendation-override': 
            
                return {
                    overrideApplied: false,
                    value: 0
                }
        
            default: 
                console.error('Invalid override key:', overrideKey)
                return null
            
        }

    }

    const ShowProgressionInfoModal = (workoutTemplateKey, phaseName, phaseIndex, slotIndex) => {

        const modalData = {
            key: ModalKey.SlotExerciseProgressionDetails,
            size: 'fullscreen',
            titleText: 'Slot Exercise Progression Details',
            workoutTemplateKey: workoutTemplateKey,
            phaseName: phaseName,
            phaseIndex: phaseIndex,
            slotIndex: slotIndex
        }

        ShowModal(modalData)

    }

    const bulkEditViewContextData = {
        OnStartOverrideDrag,
        OnOverrideDragEntersCell,
        OnEndOverrideDrag,
        GetPhaseCellStyle,
        GetSlotCellStyle,
        GetProgramTableDisplayData,
        ShowProgressionInfoModal
    }

    useEffect(() => { dispatch(resetTrainingChartState()) }, [])

    return (
        <ScreenFrame limitWidth={false}>
            <FitnessProfileInputContainer />

            <EventsInputContainer />

            <AssessmentInputContainer />

            <ProgressionModifiers />

            <OverridesInputContainer />

            <InjuriesInputContainerGenerator />

            <LoadingButton
                className="w-full flex justify-center items-center gap-x-4 bg-orange-500 rounded-full text-white font-semibold py-2"
                buttonText="Generate Program Snapshot"
                isLoading={programSnapshotLoading}
                action={() => GenerateProgramSnapshot()}
            />

            {!programSnapshotLoading && 
                <BulkEditViewContext.Provider value={bulkEditViewContextData}>

                    <ExerciseProgressionConfig exerciseProgressionConfig={latestProgramSnapshot?.slotExerciseProgressionConfig} />
                    
                    <TrainingChart traineeId={userId} />

                </BulkEditViewContext.Provider>
            }
        </ScreenFrame>
    )
}
